import React from 'react';
import {
  SidebarPanel,
  SidebarContent,
  SidebarTitle,
  SidebarSection,
  SectionHeader,
  SectionLabel,
  BetaBadge,
  ToolCard,
  ToolCardLeft,
  ToolCardIcon,
  ToolCardTextGroup,
  ToolCardName,
  ToolCardDesc,
  ChevronIcon,
  ToolCardSpinner,
  SidebarHelperText,
  GenerateButton,
  SidebarDownloadButton,
} from '../imageCanvasStyles';

const IMAGE_TOOLS = [
  { id: 'crop', icon: '&#9633;', name: 'Crop' },
  { id: 'resize', icon: '&#8596;', name: 'Resize' },
  { id: 'rotate', icon: '&#8635;', name: 'Rotate' },
  { id: 'adjust', icon: '&#9776;', name: 'Adjust' },
];

const AI_TOOLS = [
  { id: 'remove-bg', icon: '&#9741;', name: 'Remove Background', desc: 'Automatically remove image background' },
  { id: 'enhance', icon: '&#10024;', name: 'Enhance', desc: 'Improve image quality and clarity' },
];

const EditToolsSidebar = ({
  theme,
  selectedTheme,
  hasImage,
  selectedTool,
  onToolSelect,
  onNext,
  onDownload,
  isDownloading,
  aiBusyTool }) => {
  const shared = { theme, $selectedTheme: selectedTheme };
  const isAiBusy = Boolean(aiBusyTool);
  const toolsDisabled = !hasImage || isAiBusy;

  return (
    <SidebarPanel {...shared}>
      <SidebarContent>
        <SidebarTitle {...shared}>Edit Tools</SidebarTitle>

        {!hasImage && (
          <SidebarHelperText {...shared}>
            Upload an image first to enable editing tools.
          </SidebarHelperText>
        )}

        <SidebarSection>
          <SectionHeader>
            <SectionLabel {...shared}>Image Tools</SectionLabel>
          </SectionHeader>

          {IMAGE_TOOLS.map(({ id, icon, name }) => (
            <ToolCard
              key={id}
              {...shared}
              $active={selectedTool === id}
              disabled={toolsDisabled}
              onClick={() => onToolSelect(id)}
            >
              <ToolCardLeft>
                <ToolCardIcon dangerouslySetInnerHTML={{ __html: icon }} />
                <ToolCardName>{name}</ToolCardName>
              </ToolCardLeft>
              <ChevronIcon>&#8250;</ChevronIcon>
            </ToolCard>
          ))}
        </SidebarSection>

        <SidebarSection>
          <SectionHeader>
            <SectionLabel {...shared}>AI Tools</SectionLabel>
            <BetaBadge {...shared}>Beta</BetaBadge>
          </SectionHeader>

          {AI_TOOLS.map(({ id, icon, name, desc }) => {
            const isBusy = aiBusyTool === id;
            return (
              <ToolCard
                key={id}
                {...shared}
                $active={selectedTool === id || isBusy}
                disabled={toolsDisabled}
                onClick={() => onToolSelect(id)}
              >
                <ToolCardLeft>
                  <ToolCardIcon dangerouslySetInnerHTML={{ __html: icon }} />
                  <ToolCardTextGroup>
                    <ToolCardName>{name}</ToolCardName>
                    {desc && (
                      <ToolCardDesc>
                        {isBusy ? 'Processing...' : desc}
                      </ToolCardDesc>
                    )}
                  </ToolCardTextGroup>
                </ToolCardLeft>
                {isBusy
                  ? <ToolCardSpinner aria-hidden='true' />
                  : <ChevronIcon>&#8250;</ChevronIcon>}
              </ToolCard>
            );
          })}
        </SidebarSection>

        <SidebarSection>
           <SidebarDownloadButton
            {...shared}
            onClick={onDownload}
            disabled={!hasImage || isDownloading}
            title={isDownloading ? 'Downloading…' : 'Download image'}
          >
            {isDownloading ? <>&#8635;&nbsp;Downloading…</> : <>&#8659;&nbsp;Download</>}
          </SidebarDownloadButton>
          <GenerateButton {...shared} disabled={toolsDisabled} onClick={onNext}>
            Next <span aria-hidden='true'>&#8250;</span>
          </GenerateButton>
        </SidebarSection>
      </SidebarContent>
    </SidebarPanel>
  );
};

export { EditToolsSidebar };
